<script>
import CockpitMatryoshkaInheritanceTreeSection
from '@/components/cockpit/matryoshka/inheritance-tree/CockpitMatryoshkaInheritanceTreeSection.vue';
import { mapActions, mapGetters } from 'vuex';
import MarkingsAssignList from '@/components/markings/assign-list/MarkingsAssignList.vue';
import MarkingsAssignListFilters
from '@/components/markings/assign-list/MarkingsAssignListFilters.vue';

export default {
  name: 'CockpitMatryoshkaInheritanceTree',
  props: {
    labelArchiveId: Number,
    initOrderId: null,
  },
  emits: [
    'connections-updated',
    'go-to-serial-number',
  ],
  data: () => ({
    loadingDetails: true,
    itemDetails: {},
    parentItems: [],
    childrenItems: [],
    selectedChildrenItems: [],
    scannedSerialNumber: '',
    scannedSerialNumberForConnect: '',
    showLoadingOverlay: false,
    toAssignFilters: {
      searchQuery: '',
      exactSerialNumber: '',
      orderId: null,
      lineId: null,
    },
    loadingToAssignItems: false,
    pendingToAssign: false,
    pendingToDetach: false,
    toAssignItems: [],
    toAssignItemsCurrentPage: 1,
    toAssignItemsPerPage: 10,
    toAssignItemsTotal: 0,
    toAssignType: 'parent',
    assignToType: {
      parent: 'parent',
      children: 'children',
    },

    showAssignModal: false,
  }),
  components: {
    MarkingsAssignListFilters,
    MarkingsAssignList,
    CockpitMatryoshkaInheritanceTreeSection,
  },
  computed: {
    ...mapGetters([
      'globalSettings',
    ]),
    ...mapGetters('packingSettings', [
      'getUnitById',
      'getHigherLevelUnit',
      'getLowerLevelUnit',
    ]),
    ...mapGetters('emptyLabelArchive', [
      'isLabelArchiveEmpty',
    ]),
    disabledItemIds() {
      return [
        ...this.parentItems.map(i => i.id),
        ...this.childrenItems.map(i => i.id),
      ];
    },
    fetchParams() {
      return {
        plantCode: this.globalSettings.plantCode,
        query: {
          serialNumber: this.toAssignFilters.searchQuery,
          orderId: this.toAssignFilters.orderId,
          labelStatus: this.toAssignType === this.assignToType.parent
            ? 'Active'
            : 'Removed',
          exactSerialNumbers: this.toAssignFilters.exactSerialNumber,
          lineId: this.toAssignFilters.lineId,
          skip: (this.toAssignItemsCurrentPage - 1) * this.toAssignItemsPerPage,
          take: this.toAssignItemsPerPage,
        },
      };
    },
    lowerLevelUnit() {
      return this.getLowerLevelUnit(
        this.itemDetails.unitId,
        this.itemDetails.skuId,
      );
    },
    upperLevelUnit() {
      return this.getHigherLevelUnit(
        this.itemDetails.unitId,
        this.itemDetails.skuId,
      );
    },
    unit() {
      return this.getUnitById(this.itemDetails?.unitId) || {};
    },
    canConnectToUpperLevel() {
      return !!this.upperLevelUnit?.id;
    },
    canConnectToLowerLevel() {
      return !!this.lowerLevelUnit?.id;
    },
  },
  watch: {
    labelArchiveId: {
      handler() {
        this.getDetails();
      },
      immediate: true,
    },
    toAssignFilters: {
      handler() {
        this.toAssignItemsCurrentPage = 1;
      },
      deep: true,
    },
    fetchParams: {
      handler() {
        this.fetchToAssignItems();
      },
      deep: true,
    },
    showAssignModal(v) {
      if (v) {
        this.scannedSerialNumber = '';
        this.scannedSerialNumberForConnect = '';
        this.fetchToAssignItems();
      } else {
        this.scannedSerialNumber = '';
        this.scannedSerialNumberForConnect = '';
        this.toAssignItems = [];
      }
    },
  },
  methods: {
    ...mapActions('labelArchive', [
      'getLabelArchive',
      'getLabelArchiveItem',
      'getLabelArchiveByBarcodes',
      'getLabelArchiveByContainerLabel',
      'getLabelArchiveByContainedLabel',
      'attachLabelArchiveItem',
      'detachLabelArchiveItem',
      'utilizeLabelArchive',
    ]),
    async fetchLabelArchiveBySerialNumber(serialNumber) {
      const { plantCode } = this.globalSettings;
      return this.getLabelArchiveByBarcodes({
        params: {
          plantCode,
          query: {
            exactBarCodes: [serialNumber],
          },
        },
      });
    },
    toggleSelectChildrenItem(id) {
      if (!this.childrenItems.find(i => i.id === id)) return;
      if (this.selectedChildrenItems.includes(id)) {
        this.selectedChildrenItems = this.selectedChildrenItems
          .filter(i => i !== id);
      } else {
        this.selectedChildrenItems = [
          ...this.selectedChildrenItems,
          id,
        ];
      }
    },
    handleReverseSelectChildren(ids) {
      this.selectedChildrenItems = this.childrenItems
        .filter(i => !this.isLabelArchiveEmpty(i.id))
        .filter(i => !ids.includes(i.id))
        .map(i => i.id);
    },
    async fetchParents() {
      const { plantCode } = this.globalSettings;
      const { data } = await this.getLabelArchiveByContainedLabel({
        params: {
          plantCode,
          query: {
            id: this.labelArchiveId,
          },
        },
      });
      this.parentItems = data
        .filter(i => i.unitId === this.upperLevelUnit?.id);
    },
    async fetchChildren() {
      const { plantCode } = this.globalSettings;
      const { data } = await this.getLabelArchiveByContainerLabel({
        params: {
          plantCode,
          query: {
            id: this.labelArchiveId,
          },
        },
      });

      this.childrenItems = data
        .filter(i => i.unitId === this.lowerLevelUnit?.id);
    },
    async fetchLabelArchiveItem() {
      if (!this.labelArchiveId) return;

      const { plantCode } = this.globalSettings;
      const { data } = await this.getLabelArchiveItem({
        params: {
          id: this.labelArchiveId,
          plantCode,
        },
      });
      this.itemDetails = { ...data };
    },
    clearToAssignFilters() {
      this.toAssignFilters = {
        ...this.toAssignFilters,
        searchQuery: '',
        exactSerialNumber: '',
      };
    },
    async fetchToAssignItems() {
      if (!this.showAssignModal) return;

      const { plantCode } = this.globalSettings;
      this.loadingToAssignItems = true;

      let unitId = null;
      if (this.toAssignType === this.assignToType.parent) {
        unitId = this.getHigherLevelUnit(
          this.itemDetails.unitId,
          this.itemDetails.skuId,
        )?.id;
      } else {
        unitId = this.getLowerLevelUnit(
          this.itemDetails.unitId,
          this.itemDetails.skuId,
        )?.id;
      }

      const { data } = await this.getLabelArchive({
        params: {
          plantCode,
          query: {
            orderId: this.toAssignFilters.orderId,
            labelStatus: this.toAssignType === this.assignToType.parent
              ? 'Active'
              : 'Removed',
            lineId: this.toAssignFilters.lineId,
            skuId: this.itemDetails.skuId,
            exactSerialNumbers: this.toAssignFilters.exactSerialNumber || undefined,
            unitId,
            page_number: this.toAssignItemsCurrentPage,
            page_size: this.toAssignItemsPerPage,
          },
        },
      });
      this.toAssignItems = data
        .items
        .filter(i => i.id !== this.labelArchiveId);
      this.toAssignItemsTotal = data?.totalCount || 0;
      this.loadingToAssignItems = false;
    },
    async getDetails() {
      this.loadingDetails = true;

      this.selectedChildrenItems = [];
      await this.fetchLabelArchiveItem();
      await this.fetchParents();
      await this.fetchChildren();

      this.loadingDetails = false;
    },
    handleConnectNewParent() {
      this.selectedChildrenItems = [];
      this.toAssignType = this.assignToType.parent;
      this.showAssignModal = true;
    },
    handleConnectNewChildren() {
      this.selectedChildrenItems = [];
      this.toAssignType = this.assignToType.children;
      this.showAssignModal = true;
    },
    async handleDisconnectParent(id) {
      const { plantCode } = this.globalSettings;
      this.pendingToDetach = true;
      await this.detachLabelArchiveItem({
        params: {
          plantCode,
          id: this.labelArchiveId,
        },
        data: {
          destId: id,
        },
      });
      await this.getDetails();
      this.$emit('connections-updated');
      this.pendingToDetach = false;
    },
    async handleUtilizeEmpty(id) {
      const { plantCode } = this.globalSettings;
      this.pendingToDetach = true;
      await this.utilizeLabelArchive({
        params: {
          plantCode,
          id,
        },
      });
      await this.getDetails();
      this.$emit('connections-updated');
      this.pendingToDetach = false;
    },
    async handleDisconnectSelectedChildren() {
      const { plantCode } = this.globalSettings;
      this.pendingToDetach = true;
      await Promise.all(
        this.selectedChildrenItems.map(id => this.detachLabelArchiveItem({
          params: {
            plantCode,
            id,
          },
          data: {
            destId: this.labelArchiveId,
          },
        })),
      );
      this.selectedChildrenItems = [];
      await this.getDetails();
      this.$emit('connections-updated');
      this.pendingToDetach = false;
    },
    async handleDisconnectChildren(id) {
      const { plantCode } = this.globalSettings;
      this.pendingToDetach = true;
      this.selectedChildrenItems = this.selectedChildrenItems
        .filter(i => i !== id);
      await this.detachLabelArchiveItem({
        params: {
          plantCode,
          id,
        },
        data: {
          destId: this.labelArchiveId,
        },
      });
      await this.getDetails();
      this.$emit('connections-updated');
      this.pendingToDetach = false;
    },
    async handleSelectConnectItem(id) {
      if (!this.showAssignModal) return;

      const { plantCode } = this.globalSettings;
      this.pendingToAssign = true;
      switch (this.toAssignType) {
        case this.assignToType.parent:
          await this.attachLabelArchiveItem({
            params: {
              plantCode,
              id: this.labelArchiveId,
            },
            data: {
              destId: id,
            },
          });
          break;
        case this.assignToType.children:
          await this.attachLabelArchiveItem({
            params: {
              plantCode,
              id,
            },
            data: {
              destId: this.labelArchiveId,
            },
          });
          break;
        default:
          break;
      }
      await this.getDetails();
      await this.fetchToAssignItems();
      this.$emit('connections-updated');
      this.pendingToAssign = false;
      this.showAssignModal = false;
    },
    async handleClickEnter() {
      if (this.showLoadingOverlay) return;

      this.$bvToast.hide();
      this.toAssignFilters.exactSerialNumber = '';
      this.showLoadingOverlay = true;
      const number = this.showAssignModal
        ? this.scannedSerialNumberForConnect.trim()
        : this.scannedSerialNumber.trim();
      this.scannedSerialNumber = '';
      this.scannedSerialNumberForConnect = '';
      const { data } = await this.fetchLabelArchiveBySerialNumber(number);
      const item = data[0] || {};
      const id = item?.id;
      if (id) {
        if (this.showAssignModal) {
          this.toAssignFilters.exactSerialNumber = item.serialNumber;
          this.showLoadingOverlay = false;
          return;
        }

        this.toggleSelectChildrenItem(id);

        if (!this.childrenItems.find(i => i.id === id)) {
          this.$bvToast.toast(`Zeskanowany kod seryjny (${item.serialNumber})
            nie znajduje się w zawartości tego elementu (${this.unit?.name}
            nr seryjny: ${this.itemDetails.serialNumber}).`, {
            title: 'Element nie zawiera się w zawartości!',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-left',
          });
        } else if (this.selectedChildrenItems.includes(id)) {
          this.$bvToast.toast(`Zeskanowany kod seryjny (${item.serialNumber})
            został zaznaczony. (aktualnie zaznaczono ${this.selectedChildrenItems.length}/${this.childrenItems.length}
            elementów)`, {
            title: 'Zaznaczono element!',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-left',
          });
        } else {
          this.$bvToast.toast(`Zeskanowany kod seryjny (${item.serialNumber})
            został odznaczony. (aktualnie zaznaczono ${this.selectedChildrenItems.length}/${this.childrenItems.length}
            elementów)`, {
            title: 'Odznaczono element!',
            variant: 'secondary',
            solid: true,
            toaster: 'b-toaster-bottom-left',
          });
        }
      } else {
        this.$bvToast.toast(`Zeskanowanego kodu nie ma w systemie (${number}).`, {
          title: 'Nie znaleziono kodu!',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-left',
        });
      }

      this.showLoadingOverlay = false;
    },
    handleKeyDown(event) {
      if (event.key === 'Enter' || event.key === 'NumpadEnter' || event.key === '\r') {
        this.handleClickEnter();
      } else {
        const isNumber = /^[0-9]$/;
        const isLetter = /^[A-Za-z]$/;
        const isSemiColon = /^;$/;
        const isSpecial = /^[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/;
        const isDash = /^-$/;
        if (
          isNumber.test(event.key)
          || isLetter.test(event.key)
          || isSemiColon.test(event.key)
          || isDash.test(event.key)
          || isSpecial.test(event.key)
        ) {
          if (this.showAssignModal) {
            this.scannedSerialNumberForConnect += event.key;
          } else {
            this.scannedSerialNumber += event.key;
          }
        }
      }
    },
    initKeyDownListener() {
      document.addEventListener(
        'keydown',
        this.handleKeyDown,
      );
    },
    removeKeyDownListener() {
      document.removeEventListener(
        'keydown',
        this.handleKeyDown,
      );
    },
  },
  created() {
    this.toAssignFilters = {
      ...this.toAssignFilters,
      orderId: this.initOrderId,
    };
    this.initKeyDownListener();
  },
  beforeDestroy() {
    this.removeKeyDownListener();
  },
};
</script>

<template>
  <BOverlay
    :show="showLoadingOverlay"
    class="h-100"
  >
    <Loader v-if="loadingDetails" />

    <BOverlay
      v-else
      :show="pendingToDetach"
      class="h-100 d-flex flex-column"
    >
      <CockpitMatryoshkaInheritanceTreeSection
        v-if="!loadingDetails && upperLevelUnit"
        :label="`${$t('cockpit.parentElement')} - ${upperLevelUnit.name}`"
        :items="parentItems"
        :root-label-archive-id="labelArchiveId"
        upper-level
        :selected-ids="[]"
        :label-archive="itemDetails"
        :show-connect-button="canConnectToUpperLevel"
        @connect="handleConnectNewParent"
        @disconnect="handleDisconnectParent"
        @utilize-empty="handleUtilizeEmpty"
        @go-to-serial-number="$emit('go-to-serial-number', $event)"
      />

      <CockpitMatryoshkaInheritanceTreeSection
        v-if="!loadingDetails && lowerLevelUnit"
        class="pb-3"
        :label="`${$t('cockpit.childrenElements')} - ${lowerLevelUnit.name}`"
        :items="childrenItems"
        :root-label-archive-id="labelArchiveId"
        lower-level
        :label-archive="itemDetails"
        :selected-ids="selectedChildrenItems"
        :show-connect-button="canConnectToLowerLevel"
        @select-item-id="toggleSelectChildrenItem($event)"
        @connect="handleConnectNewChildren"
        @reverse-select="handleReverseSelectChildren"
        @disconnect="handleDisconnectChildren"
        @disconnect-selected="handleDisconnectSelectedChildren"
        @utilize-empty="handleUtilizeEmpty"
        @go-to-serial-number="$emit('go-to-serial-number', $event)"
      />
    </BOverlay>

    <BModal
      v-model="showAssignModal"
      hide-footer
      :title="$t('cockpit.selectItemToConnect')"
      size="xl"
    >
      <BOverlay :show="pendingToAssign">
        <div
          class="to-assign-filters-wrapper d-flex justify-content-between"
        >
          <MarkingsAssignListFilters
            v-if="false"
            v-model="toAssignFilters"
            hide-order-id
            hide-line-id
            hide-search
          />

          <BButton
            variant="secondary"
            size="sm"
            class="py-1"
            @click="clearToAssignFilters"
          >
            <i class="fas fa-sync-alt mr-1" />
            Odśwież listę
          </BButton>
        </div>

        <div class="to-assign-list-wrapper">
          <Loader
            v-if="loadingToAssignItems"
          />
          <BOverlay
            v-else
            :show="showLoadingOverlay"
          >
            <MarkingsAssignList
              :lower-level="toAssignType === assignToType.children"
              :list="toAssignItems"
              :disabled-ids="disabledItemIds"
              @select-item="handleSelectConnectItem"
            />
          </BOverlay>
        </div>

        <div class="to-assign-pagination-box">
          <BPagination
            v-model="toAssignItemsCurrentPage"
            :per-page="toAssignItemsPerPage"
            :total-rows="toAssignItemsTotal"
            size="sm"
            pills
            limit="5"
            first-number
            last-number
          />
        </div>
      </BOverlay>
    </BModal>
  </BOverlay>
</template>

<style scoped lang="scss">
.to-assign-filters-wrapper {
  padding-bottom: 1rem;
  border-bottom: 1px solid #ECECEC;
}

.to-assign-list-wrapper {
  height: calc(100vh - 290px - 10px);
  overflow-y: auto;
  margin: 1rem 0;
}

.to-assign-pagination-box {
  display: flex;
  justify-content: center;
  border-top: 1px solid #ECECEC;
  padding-top: 1rem;
}
</style>
